import { z, ZodIssueCode } from "zod";

import {
	AddressObjectInputSchema,
	BedItemInputSchema,
	OverSiteControllerPeopleNumInputSchema,
	OverSiteControllerPeoplePriceInputSchema,
	RoomInputSchema,
} from "@/lib/zod/__generated__/validation-schema";

import {
	geoLocationInputSchema,
	kidsPolicyZodSchema,
	postalCodeZodSchema,
	priceObjectZodSchemaFunc,
	requiredPrepMilliSecondBeforeDateZodSchema,
	requiredPrepMilliSecondBeforeHourZodSchema,
	s3ObjectZodSchema,
} from "@/components/features/_shared/validation-schema/validation-schema";

import type { ZodError } from "zod";

const ROOM_SIZE_MAX = 110_413; // 京都御所

export const roomInputZodSchema = RoomInputSchema.extend({
	name: z.string().min(1).max(30),
	catchCopy: z.string().max(30),
	mainKeyVisual: s3ObjectZodSchema,
	keyVisuals: z.array(s3ObjectZodSchema),

	requiredPrepMillisecondBeforeDate: requiredPrepMilliSecondBeforeDateZodSchema,
	requiredPrepMillisecondBeforeHour: requiredPrepMilliSecondBeforeHourZodSchema,

	capacityPeopleMaximum: z.number().int().min(1).max(100),
	capacityAdultMaximum: z.number().int().min(1).max(100),
	capacityKidsMaximum: z.number().int().min(0).max(100),

	maxRoomNum: z.number().int().min(1).max(1_000),

	geoLocation: geoLocationInputSchema,

	roomSize: z.number().int().min(1).max(ROOM_SIZE_MAX).nullish(),
	beds: z.array(
		BedItemInputSchema.extend({
			bedNum: z.number().int().min(1).max(100),
		}),
	),

	kidsPolicy: kidsPolicyZodSchema.nullish(),

	overSiteControllerPeopleNum: z
		.array(
			OverSiteControllerPeopleNumInputSchema.extend({
				prices: z
					.array(
						OverSiteControllerPeoplePriceInputSchema.extend({
							price: priceObjectZodSchemaFunc(1_000_000),
						}),
					)
					// 固定料金の設定があるのに料金項目が空だと、超過人数の予約が売止扱いになるため保存させない
					.min(1, { message: "1つ以上の料金設定が必要です" }),
			}),
		)
		.nullish(),

	address: AddressObjectInputSchema.extend({
		postalCode: postalCodeZodSchema.nullish(),
		pref: z.string().max(4).nullish(),
		city: z.string().max(300).nullish(),
		town: z.string().max(300).nullish(),
	})
		.nullish()
		.superRefine((data, ctx) => {
			const { postalCode, pref, city, town } = data ?? {};

			if (!postalCode && !pref && !city && !town) {
				return z.NEVER;
			}

			if (!pref) {
				ctx.addIssue({
					code: ZodIssueCode.custom,
					message: "都道府県を入力してください",
					path: ["pref"],
				});
			}

			if (!city) {
				ctx.addIssue({
					code: ZodIssueCode.custom,
					message: "市区町村を入力してください",
					path: ["city"],
				});
			}

			if (!town) {
				ctx.addIssue({
					code: ZodIssueCode.custom,
					message: "市区町村以降を入力してください",
					path: ["town"],
				});
			}
		}),
});

export type RoomInputZodSchemaType = z.infer<typeof roomInputZodSchema>;

export type RoomInputZodParseErrorType = ZodError<RoomInputZodSchemaType>;
